import { isSensitiveQueryEntry, sanitizeUrlForAnalytics } from "@/lib/privacy/urlSanitization";
import { getStoredConsent } from "@/lib/analytics/consent";
import { getCookieConsent, hasStoredCookieConsent } from "@/lib/consent";

const ATTRIBUTION_KEY = "sa-attribution";
const ATTRIBUTION_TTL = 1000 * 60 * 60 * 24 * 30; // 30 days
const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;
const CLICK_ID_FIELDS = ["gclid", "dclid", "gad_source", "gbraid", "wbraid", "fbclid", "ttclid", "msclkid", "li_fat_id"] as const;

export type AttributionData = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  dclid?: string;
  gad_source?: string;
  gbraid?: string;
  wbraid?: string;
  fbclid?: string;
  ttclid?: string;
  msclkid?: string;
  li_fat_id?: string;
  landing_page?: string;
  referrer?: string;
  first_touch?: string;
  locale?: string;
  country?: string;
};

export type AttributionSnapshot = {
  first_touch?: AttributionData;
  latest_touch?: AttributionData;
};

type StoredAttribution = AttributionSnapshot & {
  stored_at?: number;
  consent_version?: string;
  consent_updated_at?: string;
};

function attributionConsent() {
  const consent = getStoredConsent();
  if (
    consent?.analytics !== "granted" || !consent.version || !consent.updatedAt ||
    !Number.isFinite(Date.parse(consent.updatedAt)) ||
    (hasStoredCookieConsent() && !getCookieConsent().analytics)
  ) return null;
  return consent;
}

export function clearAttribution() {
  if (typeof window === "undefined") return;
  try { window.localStorage.removeItem(ATTRIBUTION_KEY); } catch { /* Storage may be unavailable. */ }
}

function filterConsentedAttribution(data: AttributionData | undefined): AttributionData | undefined {
  const sanitized = sanitizeAttributionData(data);
  if (!sanitized || getStoredConsent()?.marketing === "granted") return sanitized;
  for (const key of CLICK_ID_FIELDS) delete sanitized[key];
  if (sanitized.landing_page) sanitized.landing_page = sanitizeUrlForAnalytics(sanitized.landing_page, { removeClickIds: true });
  if (sanitized.referrer) sanitized.referrer = sanitizeUrlForAnalytics(sanitized.referrer, { removeClickIds: true });
  return sanitized;
}

function stripStorageFields(parsed: StoredAttribution): AttributionSnapshot {
  return {
    first_touch: filterConsentedAttribution(parsed.first_touch),
    latest_touch: filterConsentedAttribution(parsed.latest_touch),
  };
}

function sanitizeAttributionData(data?: AttributionData) {
  if (!data) return undefined;
  const { landing_page: landingPage, referrer, ...scalarData } = data;
  const sanitized = Object.fromEntries(
    Object.entries(scalarData).filter((entry): entry is [string, string] => {
      const value = entry[1];
      return typeof value === "string" && !isSensitiveQueryEntry(entry[0], value);
    }),
  ) as AttributionData;
  return {
    ...sanitized,
    ...(landingPage ? { landing_page: sanitizeUrlForAnalytics(landingPage) } : {}),
    ...(referrer ? { referrer: sanitizeUrlForAnalytics(referrer) } : {}),
  };
}

function isLegacyAttribution(parsed: StoredAttribution | AttributionData): parsed is AttributionData {
  return !("first_touch" in parsed) || typeof parsed.first_touch === "string";
}

function normalizeLegacyAttribution(parsed: AttributionData): AttributionSnapshot {
  const sanitized = filterConsentedAttribution(parsed);
  return {
    first_touch: sanitized,
    latest_touch: sanitized,
  };
}

function parseExisting(): AttributionSnapshot | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(ATTRIBUTION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredAttribution;
    const consent = attributionConsent();
    // Legacy records have no evidence that campaign storage was consented to.
    if (
      !consent || parsed.consent_version !== consent.version ||
      parsed.consent_updated_at !== consent.updatedAt
    ) {
      clearAttribution();
      return null;
    }
    if (parsed.stored_at && Date.now() - parsed.stored_at > ATTRIBUTION_TTL) {
      clearAttribution();
      return null;
    }
    if (isLegacyAttribution(parsed)) {
      return normalizeLegacyAttribution(parsed);
    }
    return stripStorageFields(parsed);
  } catch {
    return null;
  }
}

function readAttributionParams(params: URLSearchParams) {
  const values: Partial<AttributionData> = {};
  for (const key of [...UTM_FIELDS, ...CLICK_ID_FIELDS]) {
    const value = params.get(key);
    if (value && !isSensitiveQueryEntry(key, value)) values[key] = value.slice(0, 256);
  }
  return values;
}

function hasCampaignSignal(data: Partial<AttributionData>) {
  return [...UTM_FIELDS, ...CLICK_ID_FIELDS].some((key) => Boolean(data[key]));
}

function createTouchpoint(): AttributionData {
  const params = new URLSearchParams(window.location.search);
  const vercelRegion = (window as Window & { __VERCEL_REGION?: string }).__VERCEL_REGION;

  return {
    ...readAttributionParams(params),
    landing_page: sanitizeUrlForAnalytics(window.location.pathname + window.location.search),
    referrer: document.referrer ? sanitizeUrlForAnalytics(document.referrer) : "direct",
    first_touch: new Date().toISOString(),
    locale: navigator.language,
    country: vercelRegion || navigator.language?.split("-")[1] || undefined,
  };
}

export function captureAttributionFromUrl() {
  if (typeof window === "undefined") return;
  const consent = attributionConsent();
  if (!consent) {
    clearAttribution();
    return;
  }
  const existing = parseExisting();
  const current = filterConsentedAttribution(createTouchpoint())!;
  const shouldUpdateLatest = !existing || hasCampaignSignal(current);
  const data: StoredAttribution = {
    first_touch: existing?.first_touch ?? current,
    latest_touch: shouldUpdateLatest ? current : existing?.latest_touch ?? current,
    stored_at: Date.now(),
    consent_version: consent.version,
    consent_updated_at: consent.updatedAt,
  };

  try {
    window.localStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(data));
  } catch {
    // ignore
  }
  return stripStorageFields({ ...data });
}

export function getAttribution(): AttributionData | undefined {
  const snapshot = parseExisting();
  return snapshot?.latest_touch ?? snapshot?.first_touch ?? undefined;
}

export function getAttributionSnapshot(): AttributionSnapshot | undefined {
  return parseExisting() ?? undefined;
}

export function flattenAttributionSnapshot(snapshot?: AttributionSnapshot) {
  const first = sanitizeAttributionData(snapshot?.first_touch);
  const latest = sanitizeAttributionData(snapshot?.latest_touch);
  const values: Record<string, string | null | undefined> = {
    attribution_first_source: first?.utm_source,
    attribution_first_medium: first?.utm_medium,
    attribution_first_campaign: first?.utm_campaign,
    attribution_first_landing_page: first?.landing_page,
    attribution_first_referrer: first?.referrer,
    attribution_latest_source: latest?.utm_source,
    attribution_latest_medium: latest?.utm_medium,
    attribution_latest_campaign: latest?.utm_campaign,
    attribution_latest_landing_page: latest?.landing_page,
    attribution_latest_referrer: latest?.referrer,
    attribution_click_id:
      latest?.gclid ??
      latest?.dclid ??
      latest?.gbraid ??
      latest?.wbraid ??
      latest?.fbclid ??
      latest?.ttclid ??
      latest?.msclkid ??
      latest?.li_fat_id ??
      null,
  };
  return Object.fromEntries(
    Object.entries(values)
      .filter((entry): entry is [string, string] => typeof entry[1] === "string" && entry[1].length > 0)
      .map(([key, value]) => [key, value.slice(0, 490)]),
  );
}
